import { encryptAndUploadBlob, type BlobUploadProgress } from '../api/blobUpload';
import type { SedrFile } from '../api/sedr';
import {
  decryptFileFromChunks,
  splitPackedChunks,
  type ChunkedFileManifest,
} from './chunkedFile';
import {
  decodeBase64,
  decryptAesGcm,
  encodeBase64,
  encryptAesGcm,
  hkdfSha256,
  importAesKey,
  packEncryptedBlob,
  unpackEncryptedBlob,
} from './primitives';

const FILE_CONTENT_KEY_INFO = 'shareweb:sedr-file-key:v1';
const FILE_CONTENT_KEY_AAD = 'shareweb:sedr-file-key:v1';

async function wrapFileContentKey(
  roomKey: Uint8Array,
  encryptionFileId: string,
  contentKey: Uint8Array,
): Promise<string> {
  const material = await hkdfSha256(
    roomKey,
    new TextEncoder().encode(encryptionFileId),
    FILE_CONTENT_KEY_INFO,
    32,
  );
  const aesKey = await importAesKey(material);
  const encrypted = await encryptAesGcm(
    aesKey,
    contentKey,
    new TextEncoder().encode(FILE_CONTENT_KEY_AAD),
  );
  return encodeBase64(packEncryptedBlob(encrypted));
}

async function unwrapFileContentKey(
  roomKey: Uint8Array,
  encryptionFileId: string,
  wrappedContentKey: string,
): Promise<Uint8Array> {
  const material = await hkdfSha256(
    roomKey,
    new TextEncoder().encode(encryptionFileId),
    FILE_CONTENT_KEY_INFO,
    32,
  );
  const aesKey = await importAesKey(material);
  return decryptAesGcm(
    aesKey,
    unpackEncryptedBlob(decodeBase64(wrappedContentKey)),
    new TextEncoder().encode(FILE_CONTENT_KEY_AAD),
  );
}

export interface SedrEncryptedUploadResult {
  blobId: string;
  encryptionFileId: string;
  wrappedContentKey: string;
  contentHash: string;
  sizeBytes: number;
  chunkPlaintextBytes: number;
  chunkCount: number;
  chunkCiphertextSizes: string;
}

export async function encryptAndUploadSedrEvidence(
  file: Blob,
  roomKey: Uint8Array,
  onProgress?: (progress: BlobUploadProgress) => void,
): Promise<SedrEncryptedUploadResult> {
  const encryptionFileId = crypto.randomUUID();
  const { blobId, manifest } = await encryptAndUploadBlob(file, {
    fileId: encryptionFileId,
    onProgress,
  });
  const wrappedContentKey = await wrapFileContentKey(
    roomKey,
    encryptionFileId,
    manifest.contentKey,
  );
  const chunkCiphertextSizes = manifest.chunks.map((c) => c.ciphertextSizeBytes);
  return {
    blobId,
    encryptionFileId,
    wrappedContentKey,
    contentHash: manifest.plaintextSha256,
    sizeBytes: manifest.totalPlaintextBytes,
    chunkPlaintextBytes: manifest.chunkPlaintextBytes,
    chunkCount: manifest.chunks.length,
    chunkCiphertextSizes: JSON.stringify(chunkCiphertextSizes),
  };
}

function buildManifestFromFile(file: SedrFile, contentKey: Uint8Array): ChunkedFileManifest {
  const encryptionFileId = file.encryptionFileId ?? file.id;
  let sizes: number[] = [];
  if (file.chunkCiphertextSizes) {
    try {
      sizes = JSON.parse(file.chunkCiphertextSizes) as number[];
    } catch {
      sizes = [];
    }
  }
  const chunkCount = file.chunkCount ?? Math.max(sizes.length, 1);
  if (sizes.length === 0) {
    sizes = Array.from({ length: chunkCount }, () => 0);
  }
  return {
    fileId: encryptionFileId,
    contentKey,
    plaintextSha256: file.contentHash ?? '',
    totalPlaintextBytes: file.sizeBytes ?? 0,
    chunkPlaintextBytes: file.chunkPlaintextBytes ?? 0,
    chunks: Array.from({ length: chunkCount }, (_, chunkIndex) => ({
      chunkIndex,
      ciphertextSizeBytes: sizes[chunkIndex] ?? 0,
    })),
  };
}

/** True when the file was uploaded under the room content-key wrap (not legacy plaintext). */
export function sedrEvidenceNeedsRoomKey(file: SedrFile): boolean {
  return Boolean(file.wrappedContentKey && file.encryptionFileId);
}

export async function downloadAndDecryptSedrEvidence(
  file: SedrFile,
  roomKey?: Uint8Array | null,
): Promise<Uint8Array> {
  if (!file.blobId) {
    throw new Error('blob_missing');
  }

  // Legacy plaintext uploads (no wrapped key) — do not require a room key.
  if (!sedrEvidenceNeedsRoomKey(file)) {
    const response = await fetch(`/api/blobs/${file.blobId}`, { credentials: 'include' });
    if (!response.ok) throw new Error('download_failed');
    return new Uint8Array(await response.arrayBuffer());
  }

  if (!roomKey) {
    throw new Error('room_key_missing');
  }

  const contentKey = await unwrapFileContentKey(
    roomKey,
    file.encryptionFileId!,
    file.wrappedContentKey!,
  );
  const response = await fetch(`/api/blobs/${file.blobId}`, { credentials: 'include' });
  if (!response.ok) throw new Error('download_failed');
  const encryptedBlob = new Uint8Array(await response.arrayBuffer());
  const chunkManifest = buildManifestFromFile(file, contentKey);
  if (
    chunkManifest.chunks.length === 1
    && (chunkManifest.chunks[0]?.ciphertextSizeBytes ?? 0) === 0
  ) {
    chunkManifest.chunks = [{ chunkIndex: 0, ciphertextSizeBytes: encryptedBlob.length }];
  }
  const encryptedChunks = splitPackedChunks(
    encryptedBlob,
    chunkManifest.chunks.map((c) => c.ciphertextSizeBytes),
  );
  return decryptFileFromChunks(chunkManifest, encryptedChunks);
}
